<template>
  <div class="w-full h-full flex flex-col gap-12">
    <div
      class="w-full h-full self-center"
    >
      <doughnut-chart
        v-if="doughnutChartData"
        :data="doughnutChartData"
        :options="doughnutChartOptions"
        :plugins="doughnutChartPlugins"
        style="height: 25rem;"
      />
      <div
        v-else
        class="h-16 flex flex-col items-center justify-center text-center"
      >
        <span class="self-center text-grey-001"> No summary data found for this trip </span>
      </div>
    </div>

    <div class="w-full h-full flex flex-row gap-5">
      <div
        class="w-1/2 flex"
        style="height: 25rem;"
      >
        <div
          v-if="bargeOwnerDelayByTypeChartData"
          class="scrollable-chart-container"
        >
          <div
            class="scrollable-chart-item delay-type-owner"
          >
            <bar-chart
              :key="randomKey.bargeOwnerDelayByType"
              class="flex-grow"
              :data="bargeOwnerDelayByTypeChartData"
              :options="{
                ...verticalBarChartOptions,
                scales: {
                  ...verticalBarChartOptions.scales,
                  y: {
                    ...verticalBarChartOptions.scales.y,
                    max: maxValueBargeOwnerDelay
                  }
                }
              }"
              :plugins="verticalBarChartPlugins"
            />
          </div>
        </div>
        <div
          v-else
          class="w-full h-16 flex flex-col items-center justify-center text-center"
        >
          <span class="self-center text-grey-001"> No barge owner delay time detail found </span>
        </div>
      </div>

      <div
        class="w-1/2 flex"
        style="height: 25rem;"
      >
        <div
          v-if="chartererDelayByTypeChartData"
          class="scrollable-chart-container"
        >
          <div class="scrollable-chart-item delay-type-charterer">
            <bar-chart
              class="flex-grow"
              :key="randomKey.chartererDelayByType"
              :data="chartererDelayByTypeChartData"
              :options="{
                ...verticalBarChartOptions,
                scales: {
                  ...verticalBarChartOptions.scales,
                  y: {
                    ...verticalBarChartOptions.scales.y,
                    max: maxValueChartererDelay
                  }
                }
              }"
              :plugins="verticalBarChartPlugins"
            />
          </div>
        </div>
        <div
          v-else
          class="w-full h-16 flex flex-col items-center justify-center text-center"
        >
          <span class="self-center text-grey-001"> No barge owner delay time detail found </span>
        </div>
      </div>
    </div>

    <div class="w-full h-full flex flex-col items-center gap-5">
      <div class="w-full h-full flex flex-row gap-5">
        <div
          class="w-1/2 flex flex-col items-center gap-5"
          style="height: 25rem;"
        >
          <div class="flex flex-row items-center justify-center gap-2">
            <span class="font-semibold">
              {{
                !!isFilteredByOwner
                  ? `Top 5 Delay Activities (${chosenOwner})`
                  : 'Top 5 Delay Time by Owner (All Owner)'
              }}
            </span>
            <vs-icon
              v-if="isFilteredByOwner"
              icon="disabled_by_default"
              color="#FF3662"
              size="1.5rem"
              @click="chosenOwner = null"
              class="cursor-pointer"
            />
          </div>
          <bar-chart
            v-if="bargeOwnerDelayByOwnersChartData"
            :key="randomKey.bargeOwnerDelayByOwners"
            class="w-full h-full"
            :data="bargeOwnerDelayByOwnersChartData"
            :options="horizontalBargeChartOptions"
            :plugins="horizontalBarChartPlugins"
          />
          <div
            v-else
            class="h-16 flex flex-col items-center justify-center text-center"
          >
            <span class="self-center text-grey-001"> No top 5 delay time by owner found </span>
          </div>
        </div>
        <div
          class="w-1/2 flex flex-col items-center gap-5"
          style="height: 25rem;"
        >
          <div class="flex flex-row items-center justify-center gap-2">
            <span class="font-semibold">
              {{
                !!chosenRoute
                  ? `Top 5 Delay Activities (${chosenRoute})`
                  : 'Top 5 Delay Time by Routes (All Routes)'
              }}
            </span>
            <vs-icon
              v-if="!!chosenRoute"
              icon="disabled_by_default"
              color="#FF3662"
              size="1.5rem"
              @click="chosenRoute = null"
              class="cursor-pointer"
            />
          </div>
          <bar-chart
            v-if="chartererByRoutesChartData"
            :key="randomKey.chartererByRoutes"
            class="w-full h-full"
            :data="chartererByRoutesChartData"
            :options="horizontalBargeChartOptions"
            :plugins="horizontalBarChartPlugins"
          />
          <div
            v-else
            class="h-16 flex flex-col items-center justify-center text-center"
          >
            <span class="self-center text-grey-001"> No top 5 delay time by route found </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {filter, isArray, isEmpty, orderBy, sum, sumBy} from "lodash";

import BarChart from "@/components/barChart/index.vue";
import DoughnutChart from "@/components/doughnutChart/index.vue";
import {tooltipConfig} from "@/utils/chartUtils";
import {handleValidateAndFormatNumber} from "@/utils/inputFormatter";

export default {
  props: {
    data: {
      type: Object,
      default: () => null
    },
    isFilteredByOwner: {
      type: Boolean,
      default: false
    },
    isFilteredByRoutes: {
      type: Boolean,
      default: false
    }
  },
  components: {
    DoughnutChart,
    BarChart
  },
  watch: {
    'data.summary': {
      immediate: true,
      handler(val) {
        if (!!val) {
          if (Object.keys(val).every((key) => val[key] === null)) {
            this.doughnutChartData = null
            return
          }
          this.doughnutChartData = this.processSummaryData(val)
        } else {
          this.doughnutChartData = null
        }
      }
    },
    'data.ranks.barge_owner': {
      deep: true,
      immediate: true,
      handler(val) {
        if (!!val) {
          // PROCESS TYPES FOR DELAY BY BARGE OWNER
          if (isArray(val?.delay_type) && isEmpty(val?.delay_type)) {
            this.bargeOwnerDelayByTypeChartData = null
          } else {
            const sortedDelayTypeByBargeOwner = orderBy(
              val?.delay_type,
              'avg_delay_time_hours',
              'desc'
            )
            const filteredDelayTypeByBargeOwner = filter(
              sortedDelayTypeByBargeOwner,
              function(o) { return o.avg_delay_time_hours !== 0; }
            )

            const totalAvgDelay = filteredDelayTypeByBargeOwner.reduce((a, b) => a += b?.avg_delay_time_hours, 0)
            let total = 0
            const pareto = filteredDelayTypeByBargeOwner.map((item) => {
              total += item?.avg_delay_time_hours
              return (total / totalAvgDelay) * 100
            })

            this.maxValueBargeOwnerDelay = Math.max(...filteredDelayTypeByBargeOwner?.map(v => v?.avg_delay_time_hours)) + 100

            this.bargeOwnerDelayByTypeChartData = this.processRankData(
              filteredDelayTypeByBargeOwner,
              'key_activity_status_name',
              'key_activity_status_name',
              'avg_delay_time_hours',
              {
                backgroundColor: '#EE443F',
                barThickness: 30,
                order: 1,
                barWidth: 20,
                barCategoryGap: '100%'
              },
              {
                type: 'line',
                data: pareto,
                borderColor: '#FF7E57',
                borderWidth: 2,
                borderDash: [5, 5],
                pointRadius: 4,
                pointBorderWidth: 0,
                pointBackgroundColor: '#FF7E57',
                yAxisID: 'percentageAxis',
                order: 0
              }
            )

            if (filteredDelayTypeByBargeOwner.length < 10) {
              this.$nextTick(() => {
                const component = document.getElementsByClassName('delay-type-owner')
                if (component.length > 0) {
                  this.randomKey.bargeOwnerDelayByType = Date.now()
                  component[0].style.width = '100%'
                }
              })
            } else {
              this.$nextTick(() => {
                const component = document.getElementsByClassName('delay-type-owner')
                if (component.length > 0) {
                  this.randomKey.chartererDelayByType = Date.now()
                  component[0].style.width = '1000px'
                }
              })
            }
          }

          // PROCESS TOP 5 DELAY BY BARGE OWNER
          if (isArray(val?.owners) && isEmpty(val?.owners)) {
            this.bargeOwnerDelayByOwnersChartData = null
          } else {
            const rankData = !this.isFilteredByOwner ? val?.owners : val?.owners[0]?.avg_delay_time_details
            const labelKey = !this.isFilteredByOwner ? 'owner_code' : 'key_activity_status_name'
            this.bargeOwnerDelayByOwnersChartData = this.processRankData(
              rankData,
              labelKey,
              labelKey,
              'avg_delay_time_days',
              {
                borderWidth: 1,
                borderRadius: 5,
                barThickness: 30,
                backgroundColor: '#FF7E57',
                customId: 'owner'
              }
            )
            this.randomKey.bargeOwnerDelayByType = Date.now()
          }
        } else {
          this.bargeOwnerDelayByTypeChartData = null
          this.bargeOwnerDelayByOwnersChartData = null
        }
      }
    },
    'data.ranks.charterer': {
      deep: true,
      immediate: true,
      handler(val) {
        if (!!val) {
          // PROCESS TYPES FOR DELAY BY CHARTERER
          if (isArray(val?.delay_type) && isEmpty(val?.delay_type)) {
            this.chartererDelayByTypeChartData = null
          } else {
            const sortedDelayTypeByCharterer = orderBy(
              val?.delay_type,
              'avg_delay_time_hours',
              'desc'
            )
            const filteredDelayTypeByCharterer = filter(
              sortedDelayTypeByCharterer,
              function(o) { return o.avg_delay_time_hours !== 0; }
            )

            const totalAvgDelay = filteredDelayTypeByCharterer.reduce((a, b) => a += b?.avg_delay_time_hours, 0)
            let total = 0
            const pareto = filteredDelayTypeByCharterer.map((item) => {
              total += item?.avg_delay_time_hours
              return (total / totalAvgDelay) * 100
            })

            this.maxValueChartererDelay = Math.max(...filteredDelayTypeByCharterer?.map(v => v?.avg_delay_time_hours)) + 150

            this.chartererDelayByTypeChartData = this.processRankData(
              filteredDelayTypeByCharterer,
              'key_activity_status_name',
              'key_activity_status_name',
              'avg_delay_time_hours',
              {
                backgroundColor: '#FFAA00',
                order: 1
              },
              {
                type: 'line',
                data: pareto,
                borderColor: '#FF7E57',
                borderWidth: 2,
                borderDash: [5, 5],
                pointRadius: 4,
                pointBorderWidth: 0,
                pointBackgroundColor: '#FF7E57',
                yAxisID: 'percentageAxis',
                order: 0
              }
            )
            if (filteredDelayTypeByCharterer.length < 10) {
              this.$nextTick(() => {
                const component = document.getElementsByClassName('delay-type-charterer')
                if (component.length > 0) {
                  this.randomKey.chartererDelayByType = Date.now()
                  component[0].style.width = '100%'
                }
              })
            } else {
              this.$nextTick(() => {
                const component = document.getElementsByClassName('delay-type-charterer')
                if (component.length > 0) {
                  this.randomKey.chartererDelayByType = Date.now()
                  component[0].style.width = '1000px'
                }
              })
            }
          }

          // PROCESS TOP 5 DELAY BY ROUTES
          if (isArray(val?.routes) && isEmpty(val?.routes)) {
            this.chartererByRoutesChartData = null
          } else {
            const rankData = !this.isFilteredByRoutes ? val?.routes : val?.routes[0]?.avg_delay_time_details
            const labelKey = !this.isFilteredByRoutes ? 'route' : 'key_activity_status_name'
            this.chartererByRoutesChartData = this.processRankData(
              rankData,
              labelKey,
              labelKey,
              'avg_delay_time_days',
              {
                borderWidth: 1,
                borderRadius: 5,
                barThickness: 30,
                backgroundColor: '#FF7E57',
                customId: 'routes'
              }
            )
            this.randomKey.chartererByRoutes = Date.now()
          }
        } else {
          this.chartererDelayByTypeChartData = null
          this.chartererByRoutesChartData = null
        }
      }
    },
    isFilteredByOwner: {
      immediate: true,
      handler(val) {
        if (val) {
          this.chosenOwner = this.data.ranks.barge_owner?.owners[0]?.owner_code
        }
      }
    },
    chosenOwner: {
      handler(val) {
        this.$emit('update-owner-filter', val)
        this.$emit('update:is-filtered-by-owner', !!val)
      }
    },
    isFilteredByRoutes: {
      immediate: true,
      handler(val) {
        if (val) {
          this.chosenRoute = this.data.ranks.charterer?.routes[0]?.route
        }
      }
    },
    chosenRoute: {
      handler(val) {
        this.$emit('update:route-filter', val)
        this.$emit('update:is-filtered-by-routes', !!val)
      }
    }
  },
  computed: {
    doughnutChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        borderWidth: 0,
        plugins: {
          legend: {
            display: false
          },
          tooltip: tooltipConfig({
            callbacks: {
              title(titleItem) {
                const item = titleItem[0]
                const { label } = item
                return label
              },
              label(tooltipItem) {
                const chartData = this.chart?.data
                const datasets = chartData?.datasets

                const value = tooltipItem?.raw
                const totalSum = sum(datasets[0]?.data)
                const percentage = value / totalSum * 100
                return [
                  `${handleValidateAndFormatNumber(percentage)} %`,
                  `${handleValidateAndFormatNumber(value)} days`
                ]
              }
            }
          })
        },
        layout: {
          padding: {
            bottom: 60
          }
        }
      }
    },
    doughnutChartPlugins() {
      return [
        this.htmlLegendPlugin(),
        this.drawPercentagePlugin()
      ]
    },
    verticalBarChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        borderRadius: {
          topLeft: 5,
          topRight: 5
        },
        layout: {
          padding: {
            left: 25,
            top: 50,
            right: 25
          }
        },
        scales: {
          x: {
            offset: true,
            beginAtZero: true,
            stacked: true,
            grid: {
              display: false
            },
            ticks: {
              font: {
                weight: '600'
              },
              callback: function(value) {
                const label = this.getLabelForValue(value);
                return label.length > 5 ? `${label.slice(0, 5)}..` : label;
              },
              minRotation: 90
            },
            categoryPercentage: 1,
            barPercentage: 1
          },
          y: {
            beginAtZero: true,
            stacked: true,
            grid: {
              display: false
            },
            ticks: {
              font: {
                weight: '600'
              }
            },
            min: 0
          },
          percentageAxis: {
            offset: true,
            beginAtZero: true,
            type: 'linear',
            position: 'right',
            min: 0,
            max: 100,
            grid: {
              display: true
            },
            ticks: {
              stepSize: 25,
              font: { family: 'Poppins', weight: 'bold' },
              padding: 20,
              callback: function(value) {
                return `${value}%`
              }
            }
          }
        },
        plugins: {
          datalabels: {
            anchor: 'end',
            align: 'top',
            color: '#000',
            font: { weight: 'bold' },
            display: true
          },
          legend: {
            display: false
          },
          chartAreaBorder: {
            borderColor: 'black',
            borderWidth: 2
          },
          customTitle: {
            y: {
              display: true,
              text: 'Time (hrs)',
              color: '#000000',
              font: {
                family: 'Poppins',
                size: 12
              }
            }
          },
          axisIcons: true,
          tooltip: tooltipConfig({
            callbacks: {
              title(titleItem) {
                const item = titleItem[0]
                const { label } = item
                return label
              },
              label(tooltipItem) {
                if (tooltipItem?.dataset?.type === 'line') {
                  return `${handleValidateAndFormatNumber(tooltipItem?.raw)}%`
                }
                return `${handleValidateAndFormatNumber(tooltipItem?.raw?.y)} hrs`
              }
            }
          })
        }
      }
    },
    horizontalBargeChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            right: 50
          }
        },
        indexAxis: 'y',
        parsing: {
          xAxisKey: 'y',
          yAxisKey: 'x'
        },
        scales: {
          x: {
            type: 'linear',
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              display: false
            }
          },
          y: {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              font: {
                weight: '600'
              },
              callback: function(value) {
                const label = this.getLabelForValue(value)
                return label.length > 10 ? `${label.slice(0, 10)}..` : label;
              }
            }
          }
        },
        onClick: (e, _, chart) => {
          this.onClickBar(e, chart)
        },
        plugins: {
          legend: {
            display: false
          },
          chartAreaBorder: {
            borderColor: 'black',
            borderWidth: 2
          },
          tooltip: tooltipConfig({
            callbacks: {
              title(titleItem) {
                const item = titleItem[0]
                const { label } = item
                return label
              },
              label(tooltipItem) {
                return `${handleValidateAndFormatNumber(tooltipItem?.raw?.y)} days`
              }
            }
          })
        }
      }
    },
    verticalBarChartPlugins() {
      return [
        this.chartAreaBorderPlugin(),
        this.customTitlePlugin(),
        this.axisIconPlugin(),
        this.drawBarValuePlugin(false),
        this.rectangleAnnotatePlugin()
      ]
    },
    horizontalBarChartPlugins() {
      return [
        this.drawBarValuePlugin(),
        this.drawBarBorder()
      ]
    }
  },
  data() {
    return {
      doughnutChartData: null,
      bargeOwnerDelayByTypeChartData: null,
      chartererDelayByTypeChartData: null,
      bargeOwnerDelayByOwnersChartData: null,
      chartererByRoutesChartData: null,
      randomKey: {
        bargeOwnerDelayByType: null,
        chartererDelayByType: null,
        bargeOwnerDelayByOwners: null,
        chartererByRoutes: null
      },
      chosenOwner: null,
      chosenRoute: null,
      maxValueBargeOwnerDelay: 500,
      maxValueChartererDelay: 500
    }
  },
  methods: {
    htmlLegendPlugin() {
      return {
        id: 'custom-legend',
        afterDraw(chart) {
          const ctx = chart.ctx;
          const legendOpts = chart.options.plugins.legend;
          if (!legendOpts) return;

          const legendItems = chart.legend.legendItems;
          const chartArea = chart.chartArea;

          // Calculate position at bottom center
          const legendX = chartArea.left;
          const legendY = chartArea.bottom + 20;
          const itemWidth = 100; // Width of each legend item
          const itemHeight = 20;
          const itemsPerRow = Math.ceil(legendItems.length / 2);

          ctx.save();
          ctx.font = '12px Poppins';

          // Draw legend items in two rows
          legendItems.forEach((item, i) => {
            const row = Math.floor(i / itemsPerRow);
            const col = (i + 1) % itemsPerRow;

            const x = legendX + chartArea.width / 2  - itemWidth;
            const y = legendY + row * (itemHeight + 5);

            // Draw colored box
            ctx.beginPath();
            ctx.roundRect(x, y, 34, 10, 5);
            ctx.fillStyle = item.fillStyle;
            ctx.fill();

            // Draw text
            ctx.fillStyle = '#666';
            ctx.fillText(item.text.charAt(0).toUpperCase() + item.text.slice(1), x + 40, y + 10);
          });

          ctx.restore();
        }
      }
    },
    drawPercentagePlugin() {
      return {
        id: 'drawPercentage',
        afterDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;
          const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
          const meta = chart.getDatasetMeta(0);

          meta.data.forEach((arc, index) => {
            const value = data.datasets[0].data[index];
            const calculated = handleValidateAndFormatNumber(((value / total) * 100))
            const percentage = `${calculated}%`;

            const {x, y} = arc.tooltipPosition(); // center of each arc

            ctx.save();
            ctx.fillStyle = '#fff';
            ctx.font = '600 12px Poppins';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(percentage, x, y);
            ctx.restore();
          });
        }
      }
    },
    chartAreaBorderPlugin() {
      return {
        id: 'chartAreaBorder',
        beforeDraw(chart, args, options) {
          const {ctx, chartArea: {left, top, width, height}} = chart
          const extension = 15
          const padding = 1

          ctx.save();
          ctx.strokeStyle = options.borderColor || 'black'
          ctx.lineWidth = options.borderWidth || 2

          ctx.beginPath();
          ctx.moveTo(left, top - extension)
          ctx.lineTo(left, top + height + padding)
          ctx.stroke()

          ctx.beginPath()
          ctx.moveTo(left, top + height + padding)
          ctx.lineTo(left + width + extension, top + height + padding)
          ctx.stroke();

          ctx.restore()
        }
      }
    },
    customTitlePlugin() {
      return {
        id: 'customTitle',
        afterDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, width } } = chart

          if (options?.y?.display) {
            ctx.save()
            ctx.fillStyle = options.y.color || 'black'

            const font = options.y.font || {}
            const fontSize = font.size || 12;
            const fontFamily = font.family || 'sans-serif'
            const fontWeight = font.weight || 'normal'
            ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`

            ctx.textAlign = 'center'
            ctx.textBaseline = 'top'

            const xPos = left
            const yPos = top - fontSize * 3

            ctx.fillText(options.y.text, xPos, yPos)
            ctx.restore()
          }
        }
      }
    },
    axisIconPlugin() {
      return {
        id: 'axisIcons',
        afterDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, right, bottom }, scales } = chart;

          const topIcon = {
            text: '\u2BC5',
            x: left,
            y: top - 15,
            color: '#000',
            font: 'bold 20px FontAwesome'
          };

          const rightIcon = {
            text: '\u2BC8',
            x: right + 15,
            y: bottom + 2,
            color: '#000',
            font: 'bold 20px Poppins'
          }

          ctx.save()
          ctx.font = topIcon.font
          ctx.fillStyle = topIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(topIcon.text, topIcon.x, topIcon.y)
          ctx.restore();

          ctx.save()
          ctx.font = rightIcon.font
          ctx.fillStyle = rightIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(rightIcon.text, rightIcon.x, rightIcon.y)
          ctx.restore()
        }
      }
    },
    drawBarValuePlugin(isHorizontal = true) {
      return {
        id: 'drawBarValue',
        afterDatasetsDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;
          const meta = chart.getDatasetMeta(0);

          const padding = 20

          meta.data.forEach((arc, index) => {
            const rawValue = handleValidateAndFormatNumber(data.datasets[0].data[index].y);
            const value = isHorizontal ? `${rawValue} days` : rawValue
            const textWidth = ctx.measureText(value).width;
            const {x, y, width} = arc;
            ctx.save();
            ctx.fillStyle = '#000';
            ctx.font = 'normal 12px Poppins';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            const textX = isHorizontal ? x + padding + textWidth / 3 : 0;
            const textY = isHorizontal ? y : 0;

            if (!isHorizontal) {
              ctx.translate(x, y - 25);
              ctx.rotate(-Math.PI / 2);
            }
            ctx.fillText(value, textX, textY);
            ctx.restore();
          });
        }
      }
    },
    drawBarBorder() {
      return {
        id: 'chartAreaBorder',
        beforeDraw(chart, args, options) {
          const {ctx, chartArea: {left, top, width, height}} = chart;
          ctx.save();
          ctx.strokeStyle = options.borderColor;
          ctx.lineWidth = options.borderWidth;
          ctx.strokeRect(left - 1.5, 0, 0, height);
          ctx.restore();
        }
      }
    },
    rectangleAnnotatePlugin() {
      return {
        id: 'rectangleAnnotatePlugin',
        drawRoundedRect: function (ctx, x, top, width, height, radius) {
          const paddingTop = 15;
          const y = top - paddingTop;
          const h = height + paddingTop;

          ctx.beginPath();
          ctx.moveTo(x + radius, y);
          ctx.lineTo(x + width - radius, y);
          ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
          ctx.lineTo(x + width, y + h - radius);
          ctx.quadraticCurveTo(x + width, y + h, x + width, y + h);
          ctx.lineTo(x + radius, y + h);
          ctx.quadraticCurveTo(x, y + h, x, y + h);
          ctx.lineTo(x, y + radius);
          ctx.quadraticCurveTo(x, y, x + radius, y);
          ctx.closePath();
          ctx.fill();
        },
        afterDraw: function(chart) {
          const { ctx, chartArea: { top, height, left } } = chart;
          const meta = chart.getDatasetMeta(0)
          const { data: datasets } = chart.data.datasets[0]
          const total = sumBy(datasets, 'y')
          let top80PercentIndex
          const percentageArray = []
          for (let index = 0; index < datasets.length; index++) {
            const { y } = datasets[index]
            const percentage = y / total * 100
            percentageArray.push(percentage)
            if (sum(percentageArray) >= 80) {
              top80PercentIndex = index
              break
            }
          }

          let x1 = meta.data[0]?.x - meta.data[0]?.width
          let x2 = meta.data[top80PercentIndex]?.x + meta.data[top80PercentIndex]?.width

          if (x1 < left) {
            x1 = meta.data[0]?.x - meta.data[0]?.width / 2 -  meta.data[0]?.width / 4
            x2 = meta.data[top80PercentIndex]?.x + meta.data[0]?.width / 2 + meta.data[0]?.width / 4
          }

          const width = x2 - x1
          const radius = 10

          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = '#FFE5DD';

          this.drawRoundedRect(ctx, x1, top, width, height, radius);
          ctx.restore();
        }
      }
    },
    processSummaryData(summary) {
      const labels = Object.keys(summary).map((key) => {
        const split = key.split('_')
        const splice = split.slice(1)
        return splice
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      })

      return {
        labels: labels,
        datasets: [
          {
            data: Object.keys(summary).map(key => summary[key]),
            backgroundColor: Object.keys(summary).map((key) => {
              const split = key.split('_')
              const slice = split.slice(1)
              switch (true) {
                case slice.includes('charterer'):
                  return '#FFAA00'
                case slice.includes('owner'):
                  return '#EE443F'
                default:
                  return '#AF4DDD'
              }
            })
          }
        ]
      }
    },
    processRankData(data, labelKey, xKey, yKey, options, additionalData=null) {
      return {
        labels: data.map((item) => item[labelKey]),
        datasets: [
          {
            data: data.map((item) => ({
              x: item[xKey],
              y: item[yKey]
            })),
            ...options
          },
          ...(
            additionalData
              ? [{ ...additionalData }]
              : []
          )
        ]
      }
    },
    onClickBar(e, chart) {
      const points  = chart.getElementsAtEventForMode(e, 'nearest', { intersect: true }, true)
      if (points.length) {
        const point = points[0]
        const { data: { datasets } } = chart
        const type = datasets[0]?.customId
        const label = chart.data.labels[point.index]

        const { element: { $context: { raw: { x } } } } = point

        if (type === 'owner') {
          const owners = this.data.ranks.barge_owner.owners.map(({ owner_code }) => owner_code) || []
          const isOwnerExists = owners.includes(x)
          this.chosenOwner = isOwnerExists ? label : this.chosenOwner
        }
        if (type === 'routes') {
          const routes = this.data.ranks.charterer.routes.map(({ route }) => route) || []
          const isRouteExists = routes.includes(x)
          this.chosenRoute = isRouteExists ? label : this.chosenRoute
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">

.scrollable-chart-container {
  flex-grow: 1;
  display: flex;
  max-width: 100%;
  width: 500px;
  overflow-x: scroll;

  .scrollable-chart-item {
    flex: none;
    width: 1000px;
    height: 100%;
    display: flex;
  }
}

</style>